/**
 * @file: SortBar.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The sort control row. Sort drives the list `sorter` (the reservation list has
 * no owner/assignee, so this is the panel's lone control besides search).
 */

import { Typography } from '@euler/app/components/typography';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { kReservationSortOptions } from '@euler/app/flows/reservations/pages/list/functions/reservationListConstants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { ArrowUpDown, ChevronDown } from 'lucide-react-native';
import { memo, useCallback, useMemo } from 'react';
import { TouchableOpacity, View } from 'react-native';

export const SortBar = memo(
  ({
    sortKey,
    onSelectSort,
  }: {
    sortKey: string;
    onSelectSort: (key: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { showActionSheetWithOptions } = useActionSheet();

    const sortLabel = useMemo(
      () =>
        (
          kReservationSortOptions.find(o => o.key === sortKey) ??
          kReservationSortOptions[0]
        ).label,
      [sortKey],
    );

    const openSortSheet = useCallback(() => {
      const options = [...kReservationSortOptions.map(o => o.label), '取消'];
      showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex: kReservationSortOptions.length,
          title: '排序方式',
        },
        selected => {
          if (selected == null || selected >= kReservationSortOptions.length) {
            return;
          }
          onSelectSort(kReservationSortOptions[selected].key);
        },
      );
    }, [onSelectSort, showActionSheetWithOptions]);

    return (
      <View
        style={{
          flexDirection: 'row',
          marginHorizontal: rpx(16),
          marginBottom: rpx(14),
        }}
      >
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={openSortSheet}
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            height: rpx(44),
            paddingHorizontal: rpx(14),
            borderRadius: rpx(12),
            backgroundColor: LD.surface,
            borderWidth: 1,
            borderColor: LD.hairline,
          }}
        >
          <ArrowUpDown size={rpx(16)} color={LD.steel} />
          <Typography.Text
            size={rpx(14)}
            color={LD.ink}
            medium
            numberOfLines={1}
            style={{ flex: 1, marginLeft: rpx(8) }}
          >
            {`排序：${sortLabel}`}
          </Typography.Text>
          <ChevronDown size={rpx(16)} color={LD.mist} />
        </TouchableOpacity>
      </View>
    );
  },
);
